import axios from 'axios'

import config from 'config'
import { session } from 'auth/session'

const idToken = session.get('authIdToken')

export const apiClient = axios.create({
  baseURL: config.platform.apiUrl,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  },
})

export const coreApiClient = axios.create({
  baseURL: config.platform.coreApiUrl,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
    'sls-token-type': 'dashboardToken',
  },
})

/**
 * Stale-session recovery.
 *
 * If we have a cached, set-up user in the session but the backend no longer
 * recognizes this token's identity, the local session is dead and must be
 * cleared — otherwise the app hangs on a loading spinner with no way out.
 *
 * This happens after an SSO account re-key: an older browser session still
 * holds the pre-re-key (e.g. password) token. That token's `sub` now maps to
 * no account, so authenticated calls come back "You have not registered a user
 * account" (404); or the account is SSO-only now, so it's rejected with
 * SsoRequiredError (403).
 *
 * Scoped tightly on purpose:
 *  - only when `session.get('user')` is set (we believed we were logged in and
 *    set up — so this never fires during new-user signup, where `user` isn't
 *    set yet and a /me 404 legitimately means "go pick a username"), and
 *  - only on the specific "not registered" / SSO-required responses, so normal
 *    404s (missing resource) and 403s (RBAC) pass through untouched.
 *
 * Returns true if it handled the error (caller should stop — we're navigating
 * away), false otherwise.
 */
const handleStaleSession = (error) => {
  const status = error.response?.status
  const data = error.response?.data
  const message =
    (typeof data === 'string' ? data : data?.errorMessage || data?.message) || ''

  const isStaleIdentity =
    session.get('user') &&
    ((status === 404 && /not registered/i.test(message)) ||
      (status === 403 && /single sign-on|sso/i.test(message)))

  if (!isStaleIdentity) {
    return false
  }

  const notice =
    status === 403 && message
      ? message
      : 'Your session is no longer valid. Please sign in again.'

  session.clearAuth()
  window.location.replace(`/?error_description=${encodeURIComponent(notice)}`)
  return true
}

// Add a response interceptor
apiClient.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx
    return response?.data
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    if (handleStaleSession(error)) {
      // Navigating away to a clean login; never resolve/reject so downstream
      // error handlers don't run on a torn-down page.
      return new Promise(() => {})
    }
    return Promise.reject(error)
  }
)
// Add a response interceptor
coreApiClient.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx
    return response?.data
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    if (handleStaleSession(error)) {
      return new Promise(() => {})
    }
    return Promise.reject(error)
  }
)
